/**
 * LeadAgentCommentsStrip — collapsible full-width team-notes strip beneath the
 * Agent Workspace grid. Opens on arrival when team notes already exist (and
 * when a mention deep-link asks for it); otherwise starts collapsed with a
 * "N team notes · M document pins" summary. Expands to the full
 * project-comments-suite thread (LeadCommentThread): TipTap rich editor,
 * @mentions, threaded replies, pins, kind tags, emoji reactions, filters,
 * attachments, and a fullscreen pop-out. Document-specific discussion lives
 * on the document cards, so this is for lead-level coordination.
 */

import React, { useState, useEffect, useRef } from 'react';
import LeadCommentThread from './LeadCommentThread';
import { MessageSquare, ChevronDown, Lock } from 'lucide-react';

interface LeadAgentCommentsStripProps {
  leadId: string;
  leadName?: string;
  /** Supplied by AgentDocumentsHub — avoids duplicate Firestore listeners. */
  commentCount: number;
  pinCount: number;
  onOpenPdf?: (url: string, title: string, documentId: string, options?: { annotationId?: string; pageNumber?: number }) => void;
  onOpenImage?: (url: string, title: string, documentId: string, options?: { annotationId?: string; mimeType?: string }) => void;
  onOpenCsv?: (url: string, title: string, documentId: string, options?: { annotationId?: string; mimeType?: string }) => void;
  /** Open the strip on arrival (mention notification). */
  expandOnMount?: boolean;
  highlightCommentId?: string | null;
  onHighlightConsumed?: () => void;
}

/** Window event the tour dispatches to force-expand the strip on its step. */
export const EXPAND_AGENT_COMMENTS_EVENT = 'lctf:expandAgentComments';

const LeadAgentCommentsStrip: React.FC<LeadAgentCommentsStripProps> = ({
  leadId,
  leadName,
  commentCount,
  pinCount,
  onOpenPdf,
  onOpenImage,
  onOpenCsv,
  expandOnMount = false,
  highlightCommentId = null,
  onHighlightConsumed,
}) => {
  const [expanded, setExpanded] = useState(
    expandOnMount || !!highlightCommentId?.trim() || commentCount > 0,
  );
  const hasOpenedRef = useRef(false);
  /** Once the user opens or closes the strip, stop auto-expanding on count updates. */
  const userSetRef = useRef(false);

  useEffect(() => {
    const onExpand = () => setExpanded(true);
    window.addEventListener(EXPAND_AGENT_COMMENTS_EVENT, onExpand);
    return () => window.removeEventListener(EXPAND_AGENT_COMMENTS_EVENT, onExpand);
  }, []);

  useEffect(() => {
    if (userSetRef.current) return;
    if (expandOnMount || highlightCommentId?.trim() || commentCount > 0) setExpanded(true);
  }, [expandOnMount, highlightCommentId, commentCount]);

  // Keep the thread mounted once opened so TipTap drafts/state survive collapse.
  if (expanded) hasOpenedRef.current = true;

  const summary = `${commentCount} team note${commentCount === 1 ? '' : 's'} · ${pinCount} document pin${pinCount === 1 ? '' : 's'}`;

  return (
    <div className={`lacs-root ${expanded ? 'expanded' : 'collapsed'}`} data-tour="cw-agent-comments">
      <div className="lacs-toggle">
        <button
          type="button"
          className="lacs-toggle-main"
          onClick={() => {
            userSetRef.current = true;
            setExpanded(v => !v);
          }}
          aria-expanded={expanded}
        >
          <span className="lacs-toggle-icon"><MessageSquare size={15} /></span>
          <span className="lacs-toggle-label">Team Notes</span>
          <span className="lacs-internal-hint" title="Team only — not visible on the client portal">
            <Lock size={11} strokeWidth={2.25} aria-hidden /> Internal
          </span>
          <span className="lacs-toggle-summary">{summary}</span>
          <span className="lacs-toggle-chev"><ChevronDown size={16} /></span>
        </button>
      </div>
      {(expanded || hasOpenedRef.current) && (
        <div className="lacs-body" style={expanded ? undefined : { display: 'none' }}>
          <LeadCommentThread
            leadId={leadId}
            leadName={leadName}
            onOpenPdf={onOpenPdf}
            onOpenImage={onOpenImage}
            onOpenCsv={onOpenCsv}
            highlightCommentId={highlightCommentId}
            onHighlightConsumed={onHighlightConsumed}
          />
        </div>
      )}
    </div>
  );
};

export default LeadAgentCommentsStrip;
